<template>
  <div class="modal fade" id="sliderModal" tabindex="-1" aria-labelledby="sliderModalLabel" aria-hidden="true"
    data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg modal-custom-width">
      <div class="modal-content">
        <ModalHeader :view="view" subtitle="Article" modalId="sliderModal" @close="closeModal" />
        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <SearchInput v-model="searchTerm" class="flex-grow-1 me-3" style="max-width: 300px;" />

            <button
              class="btn btn-primary btn-lift rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm fw-medium"
              @click="openArticleForm">
              <i class="bi bi-plus-lg fs-6"></i>
              <span class="d-none d-sm-inline">Nuevo</span>
            </button>
          </div>
          <div v-if="loading" class="text-center">Cargando...</div>
          <div v-else>
            <div class="table-responsive" style="padding-bottom: 20px">
              <table ref="table" class="table table-bordered table-striped table-responsive-stack"
                :id="BUTTON_LINK_MODAL_ID">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Titulo</th>
                    <th>Link</th>
                    <th>Imagen</th>
                    <th>Fecha Creacion</th>
                    <th>Fecha Actualizacion</th>
                    <th>Estado</th>
                    <th>Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="article in articles" :key="article.id">
                    <td data-label="Name">
                      {{ article.name || "Sin datos disponibles" }}
                    </td>
                    <td data-label="Title">
                      {{ article.title || "Sin datos disponibles" }}
                    </td>
                    <td data-label="Link">
                      {{ article.link || "Sin datos disponibles" }}
                    </td>
                    <td data-label="Image">
                      <template v-if="article.image.endsWith('.webm')">
                        <video class="img-thumbnail" style="max-width: 100px; max-height: 100px" controls>
                          <source :src="article.image" type="video/webm" />
                          Tu navegador no soporta el formato de video.
                        </video>
                      </template>
                      <template v-else>
                        <img :src="article.image" alt="Image" class="img-thumbnail"
                          style="max-width: 100px; max-height: 100px" />
                      </template>
                    </td>
                    <td data-label="Created At">{{ article.created_at }}</td>
                    <td data-label="Updated At">{{ article.updated_at }}</td>
                    <td data-label="Estado">
                      <div class="custom-switch">
                        <input type="checkbox" :id="'switch-' + article.id" v-model="article.status" :true-value="2"
                          :false-value="1" @change="() => updateArticleStatusSwitch(article)" />
                        <label :for="'switch-' + article.id"></label>
                      </div>
                    </td>
                    <td data-label="Opciones" class="text-nowrap">
                      <div class="d-flex gap-2 justify-content-md-start justify-content-end">
                        <button class="btn btn-icon btn-light-primary btn-sm rounded-circle"
                          @click="editArticle(article)" title="Editar" data-bs-toggle="tooltip">
                          <i class="bi bi-pencil-fill"></i>
                        </button>

                        <button class="btn btn-icon btn-light-danger btn-sm rounded-circle"
                          @click="removeArticle(article.id)" title="Eliminar" data-bs-toggle="tooltip">
                          <i class="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <DataPagination :current-page="currentPage" :last-page="lastPage" :has-more-pages="hasMorePages"
              @fetch-views="loadArticles" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <ArticleFormModal v-if="showArticleForm" :view="view" :header="selectedArticle" @close="closeArticleForm"
    @article-submitted="handleArticleSubmit" @success="handleSuccess" @error="handleError" />
</template>

<script>
import { reactive, ref, onMounted, watch } from "vue";
import { Modal } from "bootstrap";
import ModalHeader from "@/components/shared/ModalHeader.vue";
import DataPagination from "@/components/data-display/DataPagination.vue";
import ArticleFormModal from "./ArticleFormModal.vue";
import SearchInput from "@/components/data-display/SearchInput.vue";
import userConfirmation from "@/utils/userConfirmation.js";
import {
  fetchArticles,
  deleteArticle,
  createArticle,
  updateArticle,
  updateArticleStatus,
} from "@/services/api/articleService.js";
import { usePagination } from "@/composables/usePagination";
import { useNotification } from "@/utils/notificationUtils";
import { updateStatus } from "@/utils/elements/statusUpdater.js";
import { showLoadingAlert, closeLoadingAlert } from "@/utils/loadingAlert.js";

const BUTTON_LINK_MODAL_ID = "sliderModal";

export default {
  components: {
    ModalHeader,
    DataPagination,
    SearchInput,
    ArticleFormModal,
  },
  props: {
    view: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const articles = reactive([]);
    const loading = ref(false);
    const showArticleForm = ref(false);
    const selectedArticle = ref(null);
    const searchTerm = ref("");

    const { currentPage, lastPage, hasMorePages } = usePagination();

    const { handleSuccess, handleError } = useNotification();



    const loadArticles = async (page = 1) => {
      loading.value = true;
      try {
        const response = await fetchArticles(
          props.view.id,
          page,
          10,
          searchTerm.value
        );
        articles.splice(0, articles.length, ...response.data);
        currentPage.value = response.meta.current_page || 1;
        lastPage.value = response.meta.last_page || 1;
        hasMorePages.value = currentPage.value < lastPage.value;
      } catch (error) {
        console.error("Error fetching buttonLinks:", error);
      } finally {
        loading.value = false;
      }
    };

    watch(searchTerm, () => {
      loadArticles(1);
    });

    const openArticleForm = () => {
      selectedArticle.value = null;
      showArticleForm.value = true;
    };

    const closeArticleForm = () => {
      showArticleForm.value = false;
    };

    const closeModal = () => {
      emit("close");
    };

    const editArticle = (article) => {
      selectedArticle.value = article;
      showArticleForm.value = true;
    };

    const handleArticleSubmit = async (articleData) => {
      const editOrCreate = selectedArticle.value ? "editar" : "crear";
      const confirmed = await userConfirmation(
        `¿Está seguro de ${editOrCreate} el Articulo?`
      );
      if (!confirmed) return;
      showLoadingAlert();
      try {
        if (selectedArticle.value) {
          await updateArticle(selectedArticle.value.id, articleData);
          handleSuccess("Artículo actualizado exitosamente");
        } else {
          await createArticle(articleData);
          handleSuccess("Artículo creado exitosamente");
        }
        loadArticles();
      } catch (error) {
        handleError("Error al crear o actualizar el artículo");
      } finally {
        closeLoadingAlert();
      }
    };

    const removeArticle = async (headerId) => {
      const confirmed = await userConfirmation(
        "Está seguro de eliminar el Articulo seleccionado"
      );
      if (!confirmed) return;
      try {
        await deleteArticle(headerId);
        loadArticles(currentPage.value);
      } catch (error) {
        console.error("Error eliminando article:", error);
      }
    };

    const updateArticleStatusSwitch = (article) => {
      updateStatus(article, updateArticleStatus, "article");
    };

    onMounted(async () => {
      loadArticles();
      const modalElement = document.getElementById(BUTTON_LINK_MODAL_ID);
      const modalInstance = new Modal(modalElement);
      modalInstance.show();
    });

    return {
      handleArticleSubmit,
      articles,
      loading,
      showArticleForm,
      selectedArticle,
      searchTerm,
      currentPage,
      lastPage,
      hasMorePages,
      loadArticles,
      removeArticle,
      openArticleForm,
      closeArticleForm,
      closeModal,
      editArticle,
      handleSuccess,
      handleError,
      updateArticleStatusSwitch,
    };
  },
};
</script>

<style scoped>
.modal-content {
  padding: 20px;
  background-color: #f9f9f9;
}

.modal-custom-width {
  max-width: 85%;
}

.img-thumbnail {
  object-fit: cover;
}

.custom-switch {
  display: inline-block;
  width: 43px;
  height: 28px;
  position: relative;
}

.custom-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.custom-switch label {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 50px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.custom-switch label:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: white;
  left: 1px;
  bottom: 1px;
  transition: transform 0.3s;
}

.custom-switch input:checked+label {
  background-color: #4caf50;
}

.custom-switch input:checked+label:before {
  transform: translateX(20px);
}

.table-responsive-stack {
  width: 100%;
  table-layout: fixed;
}

.table-responsive-stack th,
.table-responsive-stack td {
  word-wrap: break-word;
}

@media screen and (max-width: 768px) {
  .table-responsive-stack tr {
    display: block;
    border-bottom: 3px solid #ccc;
  }

  .table-responsive-stack th {
    display: none;
  }

  .table-responsive-stack td {
    display: block;
    width: 100%;
    text-align: right;
    position: relative;
    padding-left: 50%;
    box-sizing: border-box;
  }

  .modal-dialog {
    margin: 0;
    width: 100%;
  }

  .modal-content {
    width: 100%;
  }

  .modal-custom-width {
    max-width: 100%;
  }
}

.table-responsive {
  padding-bottom: 20px;
}

tbody {
  width: 100%;
}
</style>
